import { ModelDefinition } from "@/shared/types.js";
import { generateId, type UIMessage, type DynamicToolUIPart } from "ai";
import type { MCPPromptResult } from "../chat-input/prompts/mcp-prompts-popover";
import type { SkillResult } from "../chat-input/skills/skill-types";
import azureLogo from "/azure_logo.png";
import claudeLogo from "/claude_logo.png";
import openaiLogo from "/openai_logo.png";
import deepseekLogo from "/deepseek_logo.svg";
import googleLogo from "/google_logo.png";
import metaLogo from "/meta_logo.svg";
import mistralLogo from "/mistral_logo.png";
import ollamaLogo from "/ollama_logo.svg";
import ollamaDarkLogo from "/ollama_dark.png";
import grokLightLogo from "/grok_light.svg";
import grokDarkLogo from "/grok_dark.png";
import openrouterLogo from "/openrouter_logo.png";
import moonshotLightLogo from "/moonshot_light.png";
import moonshotDarkLogo from "/moonshot_dark.png";
import zAiLogo from "/z-ai.png";
import minimaxLogo from "/minimax_logo.svg";

export const getProviderLogoFromProvider = (
  provider: string,
  themeMode?: "light" | "dark" | "system",
): string | null => {
  switch (provider) {
    case "anthropic":
      return claudeLogo;
    case "azure":
      return azureLogo;
    case "openai":
      return openaiLogo;
    case "deepseek":
      return deepseekLogo;
    case "google":
      return googleLogo;
    case "mistral":
      return mistralLogo;
    case "ollama":
      // Return dark logo when in dark mode
      if (themeMode === "dark") {
        return ollamaDarkLogo;
      }
      // For system theme, check if document has dark class
      if (themeMode === "system" && typeof document !== "undefined") {
        const isDark = document.documentElement.classList.contains("dark");
        return isDark ? ollamaDarkLogo : ollamaLogo;
      }
      // Default to light logo for light mode or when themeMode is not provided
      return ollamaLogo;
    case "meta":
      return metaLogo;
    case "xai":
      if (themeMode === "dark") {
        return grokDarkLogo;
      }
      if (themeMode === "system" && typeof document !== "undefined") {
        const isDark = document.documentElement.classList.contains("dark");
        return isDark ? grokDarkLogo : grokLightLogo;
      }
      return grokLightLogo;
    case "custom":
      return null;
    case "openrouter":
      return openrouterLogo;
    case "moonshotai":
      if (themeMode === "dark") {
        return moonshotDarkLogo;
      }
      if (themeMode === "system" && typeof document !== "undefined") {
        const isDark = document.documentElement.classList.contains("dark");
        return isDark ? moonshotDarkLogo : moonshotLightLogo;
      }
      return moonshotLightLogo;
    case "z-ai":
      return zAiLogo;
    case "minimax":
      return minimaxLogo;
    default:
      return null;
  }
};

export const getProviderLogoFromModel = (
  model: ModelDefinition,
  themeMode?: "light" | "dark" | "system",
): string | null => {
  return getProviderLogoFromProvider(model.provider, themeMode);
};

export const getProviderColor = (provider: string) => {
  switch (provider) {
    case "anthropic":
      return "text-orange-600 dark:text-orange-400";
    case "openai":
      return "text-green-600 dark:text-green-400";
    case "deepseek":
      return "text-blue-600 dark:text-blue-400";
    case "google":
      return "text-red-600 dark:text-red-400";
    case "mistral":
      return "text-orange-500 dark:text-orange-400";
    case "ollama":
      return "text-gray-600 dark:text-gray-400";
    case "xai":
      return "text-purple-600 dark:text-purple-400";
    case "azure":
      return "text-purple-600 dark:text-purple-400";
    case "custom":
      return "bg-gradient-to-br from-teal-500 to-cyan-600";
    case "moonshotai":
      return "text-cyan-600 dark:text-cyan-400";
    case "z-ai":
      return "text-indigo-600 dark:text-indigo-400";
    case "minimax":
      return "text-pink-600 dark:text-pink-400";
    case "meta":
      return "text-blue-500 dark:text-blue-400";
    default:
      return "text-blue-600 dark:text-blue-400";
  }
};

export const DEFAULT_SYSTEM_PROMPT =
  "You are a helpful assistant with access to MCP tools.";

export const STARTER_PROMPTS: Array<{ label: string; text: string }> = [
  {
    label: "Show me connected tools",
    text: "List my connected MCP servers and their available tools.",
  },
  {
    label: "Suggest an automation",
    text: "Suggest an automation I can build with my current MCP setup.",
  },
  {
    label: "Summarize recent activity",
    text: "Summarize the most recent activity across my MCP servers.",
  },
];

export interface FormattedError {
  message: string;
  details?: string;
  code?: string;
  statusCode?: number;
  isRetryable?: boolean;
  isMCPJamPlatformError?: boolean;
}

const MCPJAM_PLATFORM_CODES = [
  "mcpjam_rate_limit",
  "mcpjam_api_error",
  "mcpjam_config_error",
];

export function formatErrorMessage(error: unknown): FormattedError | null {
  if (!error) return null;

  let errorString: string;
  if (typeof error === "string") {
    errorString = error;
  } else if (error instanceof Error) {
    errorString = error.message;
  } else {
    try {
      errorString = JSON.stringify(error);
    } catch {
      errorString = String(error);
    }
  }

  // Try to parse as JSON to extract structured error
  try {
    const parsed = JSON.parse(errorString);
    if (parsed && typeof parsed === "object") {
      // Handle structured error with code
      const code = parsed.code;
      const message = parsed.error || parsed.message || "An error occurred";

      return {
        message,
        details: parsed.details,
        code,
        statusCode: parsed.statusCode,
        isRetryable: parsed.isRetryable,
        isMCPJamPlatformError: code
          ? MCPJAM_PLATFORM_CODES.includes(code)
          : false,
      };
    }
  } catch {
    // Return as-is
  }

  return { message: errorString };
}

export const VALID_MESSAGE_ROLES: UIMessage["role"][] = [
  "system",
  "user",
  "assistant",
];

export function extractPromptMessageText(content: any): string | null {
  if (!content) return null;
  if (Array.isArray(content)) {
    const combined = content
      .map((block) =>
        block?.text && typeof block.text === "string" ? block.text : "",
      )
      .filter(Boolean)
      .join("\n")
      .trim();
    return combined || null;
  }
  if (typeof content === "object" && typeof content.text === "string") {
    const text = content.text.trim();
    return text ? text : null;
  }
  if (typeof content === "string") {
    const text = content.trim();
    return text ? text : null;
  }
  return null;
}

export function buildMcpPromptMessages(
  promptResults: MCPPromptResult[],
): UIMessage[] {
  const messages: UIMessage[] = [];

  for (const result of promptResults) {
    const promptMessages = result.result?.content?.messages;
    if (!Array.isArray(promptMessages)) continue;

    promptMessages.forEach((promptMessage: any, index: number) => {
      const text = extractPromptMessageText(promptMessage?.content);
      if (!text) return;

      const role = VALID_MESSAGE_ROLES.includes(promptMessage?.role)
        ? (promptMessage.role as UIMessage["role"])
        : ("user" as UIMessage["role"]);

      messages.push({
        id: `mcp-prompt-${result.namespacedName}-${index}-${generateId()}`,
        role,
        parts: [
          {
            type: "text",
            text: `[${result.namespacedName}] ${text}`,
          },
        ],
      });
    });
  }

  return messages;
}

/**
 * Builds UIMessages that simulate the LLM calling loadSkill tool.
 * Creates assistant messages with tool invocations instead of user messages.
 */
export function buildSkillToolMessages(
  skillResults: SkillResult[],
): UIMessage[] {
  const messages: UIMessage[] = [];

  for (const skill of skillResults) {
    if (!skill.content) continue;

    const toolCallId = `skill-load-${skill.name}-${generateId()}`;

    // Format output to match server-side loadSkill response
    const skillOutput = `# Skill: ${skill.name}\n\n${skill.content}`;

    // Build parts array
    const parts: UIMessage["parts"] = [];

    // Add loadSkill tool part
    const loadSkillPart: DynamicToolUIPart = {
      type: "dynamic-tool",
      toolCallId,
      toolName: "loadSkill",
      state: "output-available",
      input: { name: skill.name },
      output: skillOutput,
    };
    parts.push(loadSkillPart);

    // Add readSkillFile parts for selected files
    if (skill.selectedFiles && skill.selectedFiles.length > 0) {
      for (const file of skill.selectedFiles) {
        const fileToolCallId = `skill-file-${generateId()}`;

        const readFilePart: DynamicToolUIPart = {
          type: "dynamic-tool",
          toolCallId: fileToolCallId,
          toolName: "readSkillFile",
          state: "output-available",
          input: { name: skill.name, path: file.path },
          output: `# File: ${file.path}\n\n\`\`\`\n${file.content}\n\`\`\``,
        };
        parts.push(readFilePart);
      }
    }

    // Create assistant message with tool invocations
    messages.push({
      id: `assistant-skill-${skill.name}-${generateId()}`,
      role: "assistant",
      parts,
    });
  }

  return messages;
}
